import { ColDef, RowSelectedEvent } from '@ag-grid-community/core';
import { Box, Button, Flex, useBreakpointValue, useDisclosure } from '@chakra-ui/react';
import { useQuery, useQueryClient } from '@tanstack/react-query';
import { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { CustomMedicationDto } from '../../../lib/ServerApi';
import { api } from '../../../lib/api';
import { Tag } from '@/components/ui/tag';
import { Grid } from '@/components/ui/grid';
import { DialogContent, DialogRoot } from '@/components/ui/dialog';
import { CreateMedicationForm } from '@/musa/forms/shared/medications/components/modal/create-medication-form';

const defaultColumnDef: ColDef<CustomMedicationDto> = {
    flex: 1,
    resizable: true,
    floatingFilter: false,
};

export function MedicationsPage() {
    const queryClient = useQueryClient();
    const { open: isModalOpen, onOpen: onModalOpen, onClose: onModalClose } = useDisclosure();
    const navigate = useNavigate();
    const isMobile = useBreakpointValue({ base: true, lg: false });

    const query = useQuery<CustomMedicationDto[]>({
        queryKey: ['custom-medication', 'list'],
        queryFn: async () => {
            const { data } = await api.customMedication.list();
            return data;
        },
    });

    const [colDefs] = useState<ColDef<CustomMedicationDto>[]>([
        {
            field: 'name',
            headerName: 'Name',
            filter: 'agTextColumnFilter',
            suppressHeaderFilterButton: false,
            resizable: true,
            sort: 'asc',
            pinned: 'left',
            lockPinned: true,
        },
        {
            field: 'dose',
            headerName: 'Dose',
            maxWidth: 120,
            filter: 'agTextColumnFilter',
            suppressHeaderFilterButton: true,
            resizable: false,
        },
        {
            field: 'doseUnit',
            headerName: 'Unit',
            maxWidth: 100,
            filter: 'agTextColumnFilter',
            suppressHeaderFilterButton: true,
            resizable: false,
        },
        {
            field: 'route',
            headerName: 'Route',
            filter: 'agTextColumnFilter',
            suppressHeaderFilterButton: false,
            resizable: true,
        },
        {
            field: 'active',
            headerName: 'Status',
            maxWidth: 140,
            filter: 'agTextColumnFilter',
            suppressHeaderFilterButton: true,
            resizable: false,
            cellRenderer: ({ data }: { data: CustomMedicationDto }) => {
                return (
                    <Flex alignItems="center" height="100%">
                        <Tag colorPalette={data.active ? 'green' : 'red'} size="sm" textTransform="uppercase" fontWeight="700">
                            {data.active ? 'active' : 'inactive'}
                        </Tag>
                    </Flex>
                );
            },
        },
    ]);

    const handleCreated = async () => {
        await queryClient.invalidateQueries({ queryKey: ['custom-medication', 'list'] });
        onModalClose();
    };

    const handleSelect = (event: RowSelectedEvent) => {
        if (event.node.isSelected()) {
            navigate(`/settings/medications/${event.node.data.id}/edit`);
        }
    };

    return (
        <>
            <Flex px={{ base: '16px', md: 0 }} h="100%" flexDirection="column" gap="3">
                <Flex>
                    <Button size="sm" onClick={() => onModalOpen()} textDecoration="none">
                        Add Medication
                    </Button>
                </Flex>
                <Box style={{ width: '100%', height: 'calc(100% - 40px)' }}>
                    <Grid
                        key={isMobile ? 'mobile-grid' : 'grid'}
                        rowSelection="single"
                        columnDefs={colDefs}
                        rowData={query.data}
                        suppressCellFocus
                        defaultColDef={defaultColumnDef}
                        autoSizeStrategy={isMobile ? { type: 'fitCellContents' } : { type: 'fitGridWidth' }}
                        onRowSelected={handleSelect}
                    />
                </Box>
            </Flex>

            {isModalOpen && (
                <DialogRoot
                    lazyMount
                    size={{ mdDown: 'full', md: 'sm' }}
                    scrollBehavior="inside"
                    open={isModalOpen}
                    onOpenChange={({ open }) => {
                        if (!open) {
                            onModalClose();
                        }
                    }}
                    closeOnInteractOutside={false}>
                    <DialogContent colorPalette="purple">
                        <CreateMedicationForm onBack={onModalClose} onCreated={handleCreated} />
                    </DialogContent>
                </DialogRoot>
            )}
        </>
    );
}
